// Custom JS overrides — keep all customizations here instead of editing theme.js

// Wraps a tab's title button: on click, opens the associated x-modal instead of
// letting the click bubble up to <x-tabs>, which would otherwise switch to the
// (intentionally empty) tab panel for this tab.
class ModalTriggerTab extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    event.stopPropagation();

    const modalId = this.dataset.modalId;
    const modal = modalId && document.getElementById(modalId);

    modal?.show();
  };
}

if (!customElements.get('modal-trigger-tab')) {
  customElements.define('modal-trigger-tab', ModalTriggerTab);
}

// Wraps a <scroll-carousel> + <progress-bar> pair (Store section):
// 1. Keeps the progress bar in sync with the carousel's current slide via the
//    carousel's own "carousel:change" event.
// 2. Implements its own low-threshold drag-to-advance (mouse + touch, via Pointer
//    Events) instead of <scroll-carousel>'s built-in "allow-drag": that built-in
//    behavior only settles on whichever slide ends up closest once you let go, which
//    in practice means dragging more than half a slide's width to advance. Here, any
//    drag past a small threshold moves exactly one slide, so a slight swipe is enough.
// Scoped to its own subtree, so multiple instances on one page never interfere.
class StoreCarousel extends HTMLElement {
  #drag = null;

  connectedCallback() {
    this.carousel = this.querySelector('scroll-carousel');
    this.progressBar = this.querySelector('progress-bar');

    this.carousel?.addEventListener('carousel:change', this.#onCarouselChange);
    this.carousel?.addEventListener('pointerdown', this.#onPointerDown);
  }

  disconnectedCallback() {
    this.carousel?.removeEventListener('carousel:change', this.#onCarouselChange);
    this.carousel?.removeEventListener('pointerdown', this.#onPointerDown);
  }

  #onCarouselChange = (event) => {
    if (this.progressBar) {
      this.progressBar.valueNow = event.detail.index + 1;
    }
  };

  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    const carousel = this.carousel;

    carousel.setPointerCapture(event.pointerId);
    carousel.style.setProperty('scroll-snap-type', 'none');

    this.#drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: carousel.scrollLeft,
      startIndex: carousel.selectedIndex,
    };

    carousel.addEventListener('pointermove', this.#onPointerMove);
    carousel.addEventListener('pointerup', this.#onPointerUp);
    carousel.addEventListener('pointercancel', this.#onPointerUp);
  };

  #onPointerMove = (event) => {
    if (!this.#drag || event.pointerId !== this.#drag.pointerId) {
      return;
    }

    this.carousel.scrollLeft = this.#drag.startScrollLeft - (event.clientX - this.#drag.startX);
  };

  #onPointerUp = (event) => {
    if (!this.#drag || event.pointerId !== this.#drag.pointerId) {
      return;
    }

    const carousel = this.carousel;
    const delta = event.clientX - this.#drag.startX;
    const threshold = Math.max(30, carousel.clientWidth * 0.15);
    let targetIndex = this.#drag.startIndex;

    if (Math.abs(delta) > threshold) {
      targetIndex += delta > 0 ? -1 : 1;
    }

    carousel.removeEventListener('pointermove', this.#onPointerMove);
    carousel.removeEventListener('pointerup', this.#onPointerUp);
    carousel.removeEventListener('pointercancel', this.#onPointerUp);
    carousel.releasePointerCapture(event.pointerId);

    // Don't restore scroll-snap-type here: select() below animates via scrollTo({ behavior: 'smooth' }),
    // and re-enabling snap before that animation finishes makes the native snap fight the JS-driven
    // scroll, causing stutter. <scroll-carousel> already clears scroll-snap-type itself once the
    // scroll settles (its own "scrollend" handling), so just leave it be.
    this.#drag = null;
    carousel.select(targetIndex);
  };
}

if (!customElements.get('store-carousel')) {
  customElements.define('store-carousel', StoreCarousel);
}

// Variant picker required-option validation: variant-picker.liquid no longer pre-checks a
// default value for any selector style (dropdown/swatch/block/underline), so the shopper must
// actively pick one before adding to cart or checking out. This deliberately validates on
// "submit" rather than via the native HTML "required" attribute: a required-but-unchecked radio
// group blocks the browser's own "submit" event from ever firing at all (the browser aborts
// before dispatching it), especially awkward here since the dropdown style's radios live inside
// a closed <x-popover> (display: none) — so relying on native validation would make this
// impossible to reliably catch and report from script. A plain "submit" listener sidesteps that
// entirely and behaves the same for every selector style.
function getUnansweredVariantOptions(form) {
  const formId = form.getAttribute('id');
  if (!formId) {
    return [];
  }

  return Array.from(
    document.querySelectorAll(`variant-picker[form-id="${CSS.escape(formId)}"] .variant-picker__option`)
  ).filter((fieldset) => {
    const optionInputs = fieldset.querySelectorAll('input[data-option-position]');
    return optionInputs.length > 0 && !Array.from(optionInputs).some((input) => input.checked);
  });
}

function setVariantOptionError(fieldset, hasError) {
  fieldset.classList.toggle('variant-picker__option--error', hasError);
}

function reportUnansweredVariantOptions(form) {
  const unanswered = getUnansweredVariantOptions(form);
  if (unanswered.length === 0) {
    return false;
  }

  // Deliberately doesn't open the dropdown popover or move focus: doing either scrolls/jumps the
  // page to the option trigger, which can carry the viewport away from the "cart:error" banner
  // dispatched below (it lives right above the buy buttons, further down the page) before the
  // shopper has a chance to read it. The red-highlighted fieldset(s) are signal enough.
  unanswered.forEach((fieldset) => setVariantOptionError(fieldset, true));

  // Reuse <buy-buttons>'s own "cart:error" banner (assets/theme.js) instead of introducing a
  // separate alert style, since it's already listening on this same form for that event.
  form.dispatchEvent(
    new CustomEvent('cart:error', {
      bubbles: true,
      detail: { error: 'オプションを選択してください。' },
    })
  );

  return true;
}

// The dynamic checkout button (Shop Pay/PayPal/etc, rendered by `{{ form | payment_button }}` in
// buy-buttons.liquid) is Shopify's own embedded widget: it checks out using whatever variant is
// currently in the form's "id" field — the first available one by default — without ever going
// through this form's "submit"/"change" events. So instead of trying to intercept its click
// (unreliable, since it can render inside a cross-origin iframe), a same-origin overlay button is
// laid on top of it while a required option is unanswered, blocking the click at the DOM level.
function syncPaymentButtonGuard(form) {
  const formId = form.getAttribute('id');
  if (!formId) {
    return;
  }

  const hasUnanswered = getUnansweredVariantOptions(form).length > 0;

  document
    .querySelectorAll(`buy-buttons[form="${CSS.escape(formId)}"] .buy-buttons__payment-button-wrapper`)
    .forEach((wrapper) => {
      let guard = wrapper.querySelector(':scope > .buy-buttons__payment-button-guard');

      if (!hasUnanswered) {
        guard?.remove();
        return;
      }

      if (guard) {
        return;
      }

      guard = document.createElement('button');
      guard.type = 'button';
      guard.className = 'buy-buttons__payment-button-guard';
      guard.setAttribute('aria-label', 'オプションを選択してください。');
      guard.addEventListener('click', () => reportUnansweredVariantOptions(form));
      wrapper.appendChild(guard);
    });
}

document.addEventListener('change', (event) => {
  if (!event.target.matches('input[data-option-position]')) {
    return;
  }

  const fieldset = event.target.closest('.variant-picker__option');
  if (fieldset) {
    setVariantOptionError(fieldset, false);

    // Reflect the pick immediately: on a multi-option product, the server re-render triggered by
    // this same "change" event only shows a "dropdown" style trigger's real value once the whole
    // product resolves to one specific variant — this keeps the trigger in sync with what was
    // just picked while other option(s) on the same product are still unanswered.
    const pickedLabel = event.target.closest('label')?.querySelector('span');
    const trigger = fieldset.querySelector('button.select > span');
    if (pickedLabel && trigger) {
      trigger.textContent = pickedLabel.textContent;
    }
  }

  if (event.target.form) {
    syncPaymentButtonGuard(event.target.form);
  }
});

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.action.includes('/cart/add')) {
      return;
    }

    if (reportUnansweredVariantOptions(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true
);

document.querySelectorAll('variant-picker[form-id]').forEach((picker) => {
  const form = document.forms[picker.getAttribute('form-id')];
  if (!form) {
    return;
  }

  syncPaymentButtonGuard(form);

  // "product:rerender" (dispatched by <variant-picker> in theme.js) doesn't bubble, and the
  // theme's own listeners for it live directly on this same form element, so this has to be
  // attached here too rather than delegated from `document`. Registered after the theme's own
  // listeners (which perform the actual DOM swap), so the guard re-check below always sees the
  // freshly rendered markup.
  form.addEventListener('product:rerender', () => syncPaymentButtonGuard(form));
});
